// Generic mechanics behind a records page's column-visibility popup: a reader's own show/hide
// choices persisted to localStorage, toggled one at a time or all at once. Deliberately ignorant of
// *why* a column is available or what it defaults to when nothing is stored - each page supplies
// that (e.g. Slaughters' basis-dependent defaults, Status's single always-on column) via
// `isAvailable`/`defaultVisible`, so this stays the one place the storage/toggle/select-all
// bookkeeping lives instead of being copied into a third page's own composable.

import { computed, ref, type ComputedRef } from 'vue';

export type ColumnToggleOption = {
    id: string;
    label: string;
    checked: boolean;
};

type ColumnVisibilityOverrides<TColumnId extends string> = Partial<Record<TColumnId, boolean>>;

function readStoredOverrides<TColumnId extends string>(storageKey: string): ColumnVisibilityOverrides<TColumnId> {
    try {
        const raw = window.localStorage.getItem(storageKey);

        if (raw === null) {
            return {};
        }

        const parsed = JSON.parse(raw) as unknown;

        return parsed !== null && typeof parsed === 'object' ? (parsed as ColumnVisibilityOverrides<TColumnId>) : {};
    } catch {
        return {};
    }
}

function writeStoredOverrides<TColumnId extends string>(storageKey: string, overrides: ColumnVisibilityOverrides<TColumnId>): void {
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(overrides));
    } catch {
        // Private browsing, blocked storage, or a full quota - the choice just does not persist.
    }
}

export function useColumnVisibilityOverrides<TColumnId extends string>(config: {
    storageKey: string;
    columnIds: readonly TColumnId[];
    isAvailable: (id: TColumnId) => boolean;
    defaultVisible: (id: TColumnId) => boolean;
    label: (id: TColumnId) => string;
    onHide?: (id: TColumnId) => void;
}): {
    isVisible: (id: TColumnId) => boolean;
    toggleColumn: (columnId: string) => void;
    setAllColumns: (visible: boolean) => void;
    columnToggleOptions: ComputedRef<ColumnToggleOption[]>;
} {
    // Read once on setup; every change after that goes through `toggleColumn`/`setAllColumns`, which
    // keep this ref and localStorage in step rather than re-reading storage on every render.
    const overrides = ref<ColumnVisibilityOverrides<TColumnId>>(readStoredOverrides<TColumnId>(config.storageKey));

    function isVisible(id: TColumnId): boolean {
        if (!config.isAvailable(id)) {
            return false;
        }

        const stored = overrides.value[id];

        return stored !== undefined ? stored : config.defaultVisible(id);
    }

    function persist(next: ColumnVisibilityOverrides<TColumnId>) {
        overrides.value = next;
        writeStoredOverrides(config.storageKey, next);
    }

    function toggleColumn(columnId: string) {
        const id = columnId as TColumnId;
        const next = { ...overrides.value, [id]: !isVisible(id) };

        persist(next);

        if (!isVisible(id)) {
            config.onHide?.(id);
        }
    }

    function setAllColumns(visible: boolean) {
        const availableIds = config.columnIds.filter((id) => config.isAvailable(id));
        const next = { ...overrides.value };

        for (const id of availableIds) {
            next[id] = visible;
        }

        persist(next);

        if (!visible) {
            for (const id of availableIds) {
                config.onHide?.(id);
            }
        }
    }

    // Only the columns currently on offer - a gated column with nothing to show is left out of the
    // list entirely, not merely shown unchecked.
    const columnToggleOptions = computed<ColumnToggleOption[]>(() =>
        config.columnIds.filter((id) => config.isAvailable(id)).map((id) => ({ id, label: config.label(id), checked: isVisible(id) }))
    );

    return { isVisible, toggleColumn, setAllColumns, columnToggleOptions };
}
